import { DG } from '@cotera/sdk/core';
import {
  BaseModel,
  ReadonlyWatchable,
  TargetableModel,
  Watchable,
} from '../../actions/framework';
import { Assert } from '@cotera/utilities';
import {
  TaxonomyNode,
  convertExpressionToTaxonomy,
} from '@cotera/client/app/components/taxonomy-type-builder';
import { v4 } from 'uuid';
import { Result } from 'neverthrow';
import { EraQL, Relation } from '@cotera/era';

/**
 * The state of a taxonomy column UI.
 *
 */
export class TaxonomyColumnModel extends BaseModel implements TargetableModel {
  isTargeted: boolean;

  /**
   * Whether this UI represents a new taxonomy column or not.
   */
  readonly isNew: boolean;

  /**
   * The id of the relation containing the taxonomy column being represented.
   */
  readonly datasetName: string;

  /**
   * The id name of this column.
   */
  readonly currentNameInGraph: Watchable<string>;

  readonly formState: Watchable<{ name: string }>;

  /**
   * The eraQL defining the current taxonomy column.
   */
  readonly eraQL: Watchable<string | null>;

  /**
   * The TaxonomyNode derived from the defining taxonomy eraQL.
   */
  readonly uiState: ReadonlyWatchable<TaxonomyNode>;

  /**
   * The dataset Relation containing this taxonomy.
   */
  readonly datasetRel: ReadonlyWatchable<Result<
    DG.Graph.RelItem,
    DG.Graph.GraphError
  > | null>;

  /**
   * A derived Relation whose columns are the taxonomy column
   * and any of the taxonomy column's dependents.
   */
  readonly preview = Watchable.fromValue<Relation | null>(null);

  readonly changes: ReadonlyWatchable<Record<string, boolean>>;

  constructor(
    readonly inputs: {
      dataset: string;
      column: string;
      graph: ReadonlyWatchable<DG.BuildExplorer>;
    },
    opts: {
      targeted: boolean;
      isNew: boolean;
    }
  ) {
    super(`${inputs.dataset}-${inputs.column}`);

    this.isTargeted = opts.targeted;
    this.isNew = opts.isNew;
    this.datasetName = inputs.dataset;

    this.currentNameInGraph = Watchable.fromValue(inputs.column);

    this.formState = Watchable.fromValue({
      name: this.currentNameInGraph.snapshot(),
    });

    this.eraQL = Watchable.fromValue(
      (() => {
        const config =
          inputs.graph.snapshot().getColumn({
            rel: inputs.dataset,
            name: this.currentNameInGraph.snapshot(),
          })?.config ?? null;

        if (config == null) {
          Assert.assert(this.isNew);
          return null;
        }

        Assert.assert(config.t === 'eraql');

        return config.source;
      })()
    );

    this.datasetRel = Watchable.from((get) => {
      const graph = get(inputs.graph);

      const datset = graph.forSymbol(inputs.dataset);

      if (datset === null) {
        return null;
      }

      return datset.builtNode
        .map((r) => {
          Assert.assert(r.item.t === 'rel');
          return r.item;
        })
        .mapErr((e) => e.err);
    });

    this.uiState = Watchable.from((get) => {
      const datasetRel = get(this.datasetRel);
      const eraQL = get(this.eraQL);

      if (datasetRel === null || eraQL == null) {
        Assert.assert(this.isNew);
        return emptyTaxonomy();
      }

      if (datasetRel.isErr()) {
        // TODO handle
        return emptyTaxonomy();
      }

      const expr = EraQL.parseExpr(eraQL, {
        attributes: datasetRel.value.rel.attributes,
      });

      if (expr.isErr()) {
        // TODO handle
        return emptyTaxonomy();
      }

      return convertExpressionToTaxonomy(expr.value.ir(), datasetRel.value.rel);
    });

    this.changes = Watchable.from((_get) => {
      return {}; // TODO implement
    });
  }

  target(): void {
    this.isTargeted = true;
  }
  markAsNotTargeted(): void {
    this.isTargeted = false;
  }
}

const emptyTaxonomy = (): TaxonomyNode => ({
  name: 'root',
  children: {},
  t: 'inline',
  id: v4(),
});
